import {useEffect, useMemo, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {FaChevronLeft, FaChevronRight} from 'react-icons/fa';

import {useCart} from '../store/cartContext';
import {useNotify} from '../store/notifyContext';
import styles from '../styles/ProductPage.module.css';
import {getProductById} from '../api/catalog';
import productPlaceholder from '../assets/product-placeholder.svg';

const FALLBACK_IMAGE = productPlaceholder;

export default function ProductPage() {
    const {id} = useParams();
    const {cart, addToCart, decreaseCount, pendingIds} = useCart();
    const notify = useNotify();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const touchStartX = useRef(null);
    const initialTitleRef = useRef(typeof document !== 'undefined' ? document.title : '');

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                setLoading(true);
                setError('');
                const data = await getProductById(id);
                if (cancelled) return;
                setProduct(data);
            } catch (e) {
                if (cancelled) return;
                setError(e.message || 'Товар не найден');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    useEffect(() => {
        setActiveImageIndex(0);
    }, [product?.id]);

    useEffect(() => {
        if (error) notify.error(error);
    }, [error, notify]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (product?.name) {
            document.title = `${product.name} — Каталог`;
            return;
        }
        if (loading) {
            document.title = 'Загрузка товара — Каталог';
            return;
        }
        document.title = 'Товар — Каталог';
    }, [product?.name, loading]);

    useEffect(() => {
        const initialTitle = initialTitleRef.current;
        return () => {
            if (typeof document !== 'undefined' && initialTitle) {
                document.title = initialTitle;
            }
        };
    }, []);

    const galleryImages = useMemo(() => {
        if (!product) return [FALLBACK_IMAGE];
        const prepared = Array.isArray(product.images)
            ? product.images.filter(Boolean)
            : [];
        return prepared.length > 0 ? prepared : [FALLBACK_IMAGE];
    }, [product]);

    const hasGallery = galleryImages.length > 1;
    const activeImage = galleryImages[Math.min(activeImageIndex, galleryImages.length - 1)] || FALLBACK_IMAGE;
    const extraCharacteristics = useMemo(() => {
        const list = Array.isArray(product?.characteristics) ? product.characteristics : [];
        return list.filter((item) => item?.name && item?.value);
    }, [product?.characteristics]);

    function showPrevImage() {
        setActiveImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
    }

    function showNextImage() {
        setActiveImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
    }

    function onTouchStart(event) {
        touchStartX.current = event.changedTouches?.[0]?.clientX ?? null;
    }

    function onTouchEnd(event) {
        if (touchStartX.current == null) return;
        const endX = event.changedTouches?.[0]?.clientX ?? touchStartX.current;
        const delta = endX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) < 40 || !hasGallery) return;
        if (delta > 0) showPrevImage();
        else showNextImage();
    }

    function formatMoney(value) {
        return Number(value || 0).toLocaleString('ru-RU');
    }

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.breadcrumbs}>
                        <span className={`${styles.skLine} ${styles.skBread}`}/>
                    </div>
                    <div className={styles.topLayout}>
                        <section className={styles.galleryCard}>
                            <div className={styles.skImage}/>
                            <div className={styles.skThumbRow}>
                                <span className={styles.skThumb}/>
                                <span className={styles.skThumb}/>
                                <span className={styles.skThumb}/>
                            </div>
                        </section>
                        <section className={`${styles.detailsCard} ${styles.specsCard}`}>
                            <span className={`${styles.skLine} ${styles.skTitle}`}/>
                            <span className={`${styles.skLine} ${styles.skMeta}`}/>
                            <span className={`${styles.skLine} ${styles.skMeta}`}/>
                            <span className={`${styles.skLine} ${styles.skMeta}`}/>
                        </section>
                        <aside className={styles.buyCard}>
                            <span className={`${styles.skLine} ${styles.skTitle}`}/>
                            <span className={`${styles.skLine} ${styles.skPrice}`}/>
                            <span className={`${styles.skLine} ${styles.skBadge}`}/>
                            <span className={styles.skButton}/>
                        </aside>
                    </div>
                    <section className={styles.descriptionCard}>
                        <span className={`${styles.skLine} ${styles.skTitle}`}/>
                        <span className={`${styles.skLine} ${styles.skText}`}/>
                        <span className={`${styles.skLine} ${styles.skText}`}/>
                    </section>
                </div>
            </div>
        );
    }

    if (error || !product) {
        return <div className={styles.notFound}>{error || 'Товар не найден'}</div>;
    }

    const cartItem = cart.find(item => item.id === product.id);
    const count = cartItem ? cartItem.count : 0;
    const stockNum = product?.stock == null ? null : Number(product.stock);
    const inStock = stockNum == null ? true : stockNum > 0;
    const canInc = stockNum == null ? true : count < stockNum;
    const isPending = pendingIds?.has(product.id);

    const safeProductForCart = {
        ...product,
        image_url: galleryImages[0] || FALLBACK_IMAGE,
    };

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={styles.breadcrumbs}>
                    <Link to="/catalog" className={styles.backLink}>Каталог</Link>
                    <span className={styles.breadcrumbDot}>/</span>
                    <span className={styles.currentCrumb}>{product.name}</span>
                </div>

                <div className={styles.topLayout}>
                    <section className={styles.galleryCard}>
                        <div
                            className={styles.mainImageWrap}
                            onTouchStart={onTouchStart}
                            onTouchEnd={onTouchEnd}
                        >
                            <img
                                src={activeImage}
                                alt={product.name}
                                className={styles.mainImage}
                                onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src = FALLBACK_IMAGE;
                                }}
                            />

                            {hasGallery ? (
                                <>
                                    <button
                                        type="button"
                                        className={`${styles.galleryNav} ${styles.galleryPrev}`}
                                        onClick={showPrevImage}
                                        aria-label="Предыдущее фото"
                                    >
                                        <FaChevronLeft/>
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.galleryNav} ${styles.galleryNext}`}
                                        onClick={showNextImage}
                                        aria-label="Следующее фото"
                                    >
                                        <FaChevronRight/>
                                    </button>
                                    <div className={styles.imageCounter}>
                                        {activeImageIndex + 1} из {galleryImages.length}
                                    </div>
                                </>
                            ) : null}
                        </div>

                        {hasGallery ? (
                            <div className={styles.thumbGrid}>
                                {galleryImages.map((imageUrl, index) => (
                                    <button
                                        key={`${imageUrl}-${index}`}
                                        type="button"
                                        onClick={() => setActiveImageIndex(index)}
                                        className={`${styles.thumbButton} ${index === activeImageIndex ? styles.thumbButtonActive : ''}`}
                                        aria-label={`Показать фото ${index + 1}`}
                                    >
                                        <img
                                            src={imageUrl}
                                            alt=""
                                            className={styles.thumbImage}
                                            onError={(event) => {
                                                event.currentTarget.onerror = null;
                                                event.currentTarget.src = FALLBACK_IMAGE;
                                            }}
                                        />
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </section>

                    <section className={`${styles.detailsCard} ${styles.specsCard}`}>
                        <h2 className={styles.sectionTitle}>Характеристики</h2>
                        <div className={styles.metaRows}>
                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Категория</span>
                                <span className={styles.metaValue}>{product?.category?.name || 'Не указана'}</span>
                            </div>
                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Бренд</span>
                                <span className={styles.metaValue}>{product?.brand?.name || 'Не указан'}</span>
                            </div>
                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Артикул</span>
                                <span className={styles.metaValue}>{product?.sku || '—'}</span>
                            </div>
                            {extraCharacteristics.map((item, index) => (
                                <div className={styles.metaRow} key={`${item.name}-${item.value}-${index}`}>
                                    <span className={styles.metaLabel}>{item.name}</span>
                                    <span className={styles.metaValue}>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    <aside className={styles.buyCard}>
                        <h1 className={styles.title}>{product.name}</h1>

                        <div className={styles.price}>{formatMoney(product.price)} ₽</div>

                        <div className={`${styles.stockBadge} ${inStock ? styles.stockIn : styles.stockOut}`}>
                            {inStock ? (stockNum == null ? 'В наличии' : `В наличии: ${stockNum}`) : 'Нет в наличии'}
                        </div>

                        <div className={styles.actionCard}>
                            {count === 0 ? (
                                <button
                                    className={styles.cartBtn}
                                    onClick={() => addToCart(safeProductForCart)}
                                    type="button"
                                    disabled={isPending || !inStock}
                                >
                                    В корзину
                                </button>
                            ) : (
                                <div className={styles.countBlock}>
                                    <button
                                        className={styles.countBtn}
                                        onClick={() => decreaseCount(product.id)}
                                        type="button"
                                        disabled={isPending}
                                    >
                                        −
                                    </button>
                                    <span className={styles.countNum}>{count}</span>
                                    <button
                                        className={styles.countBtn}
                                        onClick={() => addToCart(safeProductForCart)}
                                        type="button"
                                        disabled={isPending || !inStock || !canInc}
                                        title={!canInc ? 'Достигнут лимит остатка' : 'Добавить'}
                                    >
                                        +
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className={styles.buyInfoList}>
                            <div className={styles.buyInfoItem}>Доставка по городу и по России</div>
                            <div className={styles.buyInfoItem}>Консультация по материалам</div>
                            <div className={styles.buyInfoItem}>Подбор под задачу по ремонту</div>
                        </div>
                    </aside>
                </div>

                <section className={styles.descriptionCard}>
                    <h2 className={styles.sectionTitle}>Описание</h2>
                    <p className={styles.description}>
                        {product.description || 'Описание для этого товара пока не заполнено.'}
                    </p>
                </section>
            </div>
        </div>
    );
}
